// @flow
import {Map as ImmutableMap, Record} from 'immutable';
import type {RecordFactory, RecordOf} from 'immutable';

import type {QcsSyncResourceStoreProps} from 'data/qcsSyncResourceTypes';
import {getLogger} from 'utils/logger';
import type {Account} from "data/accounts/types";

const log = getLogger('data/goals/types');

type GoalsStoreProps = QcsSyncResourceStoreProps & {};
export const mkGoalsStore: RecordFactory<GoalsStoreProps> =
  Record({
    resourcesById: ImmutableMap(),

    lastSyncDate: undefined,
    loadPending: true,

    isLoading: false,
    pendingAmount: undefined,
  });

export type GoalTypes =
  'SAVE_FOR_EMERGENCY_FUND'
  | 'SAVE_FOR_OTHER'
  | 'SAVE_FOR_WEDDING'
  | 'SAVE_FOR_HOME'
  | 'SAVE_FOR_VACATION'
  | 'SAVE_FOR_CAR';

// ////////////////////////////////////////////////////////////////////////////////////////////////
// Goal - Base Goal Properties
// ////////////////////////////////////////////////////////////////////////////////////////////////

type GoalProps = {
  type: GoalTypes,
  id: string,
  clientId: ?string,

  accountId: ?string,

  name: string,
  targetAmount: number,
  startOn: string,

  targetOn: string,
  imageId: string,
  contributionAmount: ?number,
  contributionFrequency: ?string,
  tagId: ?string,
  notes: ?string,
  description: ?string,
  savedSoFar: ?number,
  completedOn: ?string,
  initialAccount: ?Account,
  isDeleted: ?boolean,
  modifiedAt: ?number,

  isTakenFromF2S: ?boolean,
}
export type Goal = RecordOf<GoalProps>;

// WARNING - If you provide any default goals props here, you need to explicitly remove them in
// mkUpdateGoal below, to prevent overwriting these values when someone is trying to update only
// specific fields in a goal.
//
const defaultGoalProps = ({
  id: undefined,
  clientId: undefined,
  accountId: undefined,

  name: undefined,

  contributionAmount: undefined,
  contributionFrequency: undefined,

  tagId: undefined,
  targetAmount: undefined,
  targetOn: undefined,
  startOn: undefined,
  notes: undefined,
  imageId: undefined,
  description: undefined,
  completedOn: undefined,
  initialAccount: undefined,
  isDeleted: false,
  modifiedAt: undefined,
  savedSoFar: undefined,

  isTakenFromF2S: undefined,
});

// ================================================================================================
// Save for __________  Properties & Types
// ================================================================================================

export const mkSaveForOther: RecordFactory<defaultGoalProps> =
  Record({
    ...defaultGoalProps,
    type: 'SAVE_FOR_OTHER',
  });
export const mkSaveForWedding: RecordFactory<defaultGoalProps> =
  Record({
    ...defaultGoalProps,
    type: 'SAVE_FOR_WEDDING',
  });
export const mkSaveForCar: RecordFactory<defaultGoalProps> =
  Record({
    ...defaultGoalProps,
    type: 'SAVE_FOR_CAR',
  });
export const mkSaveForHome: RecordFactory<defaultGoalProps> =
  Record({
    ...defaultGoalProps,
    type: 'SAVE_FOR_HOME',
  });

export const mkSaveForEmergencyFund: RecordFactory<defaultGoalProps> =
  Record({
    ...defaultGoalProps,
    type: 'SAVE_FOR_EMERGENCY_FUND',
  });

export const mkSaveForVacation: RecordFactory<defaultGoalProps> =
  Record({
    ...defaultGoalProps,
    type: 'SAVE_FOR_VACATION',
  });

export const mkGoal = (type: string, options: any) => {
  switch (type) {
    case 'SAVE_FOR_OTHER':
      return mkSaveForOther(options);
    case 'SAVE_FOR_EMERGENCY_FUND':
      return mkSaveForEmergencyFund(options);
    case 'SAVE_FOR_WEDDING':
      return mkSaveForWedding(options);
    case 'SAVE_FOR_CAR':
      return mkSaveForCar(options);
    case 'SAVE_FOR_HOME':
      return mkSaveForHome(options);
    case 'SAVE_FOR_VACATION':
      return mkSaveForVacation(options);
    default:
      log.warn('Was a new Goal Type added? Need to update mkGoal');
      return mkSaveForOther(options);
  }
};

export const mkUpdateGoal = (type: string, options: any) => {
  const x = mkGoal(type, {
    ...options,
  });
  return x;
};

const defaultGoalBalanceProps = ({
  accountId: undefined,
  goalType: 'SAVE_FOR_OTHER',
  grossBalance: 0,
  netBalance: 0,
  balanceBreakdown: undefined,
});

const defaultGoalSubBalanceProps = ({
  accountId: undefined,
  grossBalance: 0,
  netBalance: 0,
  grossContribPct: 0,
  netContribPct: 0,
});

// SubBalance is the balance for each incoming account
type GoalSubBalanceProps = {
  accountId: string,
  grossBalance: number,
  netBalance: number,
  grossContribPct: number,
  netContribPct: number,
}
export type GoalSubBalance = RecordOf<GoalSubBalanceProps>;

// GoalBalance is the parent balance object for each goal
type GoalBalanceProps = {
  accountId: string,
  goalType: GoalTypes,
  grossBalance: number,
  netBalance: number,
  balanceBreakdown: Map<GoalSubBalance>,
}
export type GoalBalance = RecordOf<GoalBalanceProps>;

export const mkGoalSubBalance: RecordFactory<defaultGoalSubBalanceProps> = Record({...defaultGoalSubBalanceProps});

export const mkGoalBalance: RecordFactory<defaultGoalBalanceProps> = Record({...defaultGoalBalanceProps});
